import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";
import { HasAccess } from "@/components/common/has-access";
import { UsageCard } from "./usage-card";
import { UserProfile } from "@/pages/users/types/user";
import { useToast } from "@/hooks/use-toast";
import { useUpdateUtilizedSessions } from "../hooks/use-update-utilized-sessions";
import { useUsageException } from "../hooks/use-usage-exception";
import { useFreeSession } from "../hooks/use-free-session";
import { getExceptionScheduleLink } from "@/helpers/get-exp-schedule-link";

interface BundleUsageSectionProps {
  user: UserProfile;
  onCreatePackClick: () => void;
}

export function BundleUsageSection({ user, onCreatePackClick }: BundleUsageSectionProps) {
  const { toast } = useToast();
  const { mutate: updateUtilized } = useUpdateUtilizedSessions();
  const { mutate: updateUsageException } = useUsageException();
  const { mutate: addFreeSession } = useFreeSession();

  return (
    <Card className="h-[calc(100vh-20rem)] md:h-[calc(100vh-16rem)] overflow-hidden">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Bundle Usage</CardTitle>
            <CardDescription>All bundles linked to the user</CardDescription>
          </div>
          <HasAccess permission="write">
            <Button
              className="bg-sidebar"
              onClick={onCreatePackClick}
            >
              Create Pack
            </Button>
          </HasAccess>
        </div>
      </CardHeader>
      <ScrollArea className="h-[calc(100%-5rem)]">
        <CardContent className="space-y-4">
          {user.bundles_usage.map((bundle) => (
            <UsageCard
              key={bundle.id}
              bundle={bundle}
              user={user}
              id={user.id}
              updateUsageException={(usageId: string) => {
                const link = getExceptionScheduleLink({
                  standard_id: bundle.bundle.standard_id,
                  usage_id: bundle.id,
                  session_event_type: user.id,
                });
                updateUsageException(bundle.id, {
                  onSuccess: () => {
                    toast({
                      title: "Usage exception updated successfully",
                    });
                  },
                  onError: () => {
                    toast({
                      title: "Failed to update usage exception",
                      variant: "destructive",
                    });
                  },
                });
              }}
              updateUtilized={(params: any) => {
                updateUtilized(params, {
                  onSuccess: () => {
                    toast({
                      title: "Utilized sessions updated successfully",
                    });
                  },
                  onError: () => {
                    toast({
                      title: "Failed to update utilized sessions",
                      variant: "destructive",
                    });
                  },
                });
              }}
              addFreeSession={(usage_id: string, userId: string, newSessionCount: number) => {
                addFreeSession(
                  { usage_id: usage_id, userId: userId, newSessionCount: newSessionCount },
                  {
                    onSuccess: () => {
                      toast({
                        title: "Free session added successfully",
                      });
                    },
                    onError: () => {
                      toast({
                        title: "Failed to add free session",
                        variant: "destructive",
                      });
                    },
                  }
                );
              }}
            />
          ))}
          {user.bundles_usage.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <CalendarDays className="h-8 w-8 text-muted-foreground" />
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  No bundles found
                </p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </ScrollArea>
    </Card>
  );
}

